import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { VideoTopicCard, AmpTopicCard, InlineTopicThread, CollapsibleMessage } from '../DiscussionsScreen/DiscussionsScreen';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import SetHomepageButton from '../SetHomepageButton/SetHomepageButton';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import ReactionButton from '../shared/ReactionButton';
import './NewsFeedScreen.css';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(config => {
  const token = safeStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000);
  if (seconds < 60)          return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)          return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)            return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)              return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5)             return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12)           return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

const SECTION_LABELS = {
  description:    { label: 'Description',          icon: '📝' },
  maintenance:    { label: 'Maintenance & Tips',    icon: '🔧' },
  variants:       { label: 'Variants & Revisions',  icon: '🔀' },
  notable_users:  { label: 'Notable Users',         icon: '🎸' },
  amp_builders:   { label: 'Amp Builders',          icon: '🛠️' },
};

function avatarInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name[0].toUpperCase();
}

function stripHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

const Avatar = ({ name, size = 'medium' }) => (
  <div className={`nfs-avatar${size === 'small' ? ' nfs-avatar--small' : ''}`}>
    {avatarInitials(name)}
  </div>
);

const WhatsOnYourMindBar = ({ user, onClick }) => {
  const displayName = user?.nickname || user?.username || '';
  return (
    <div
      className="nfs-composer-bar"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    >
      <Avatar name={displayName} size="small" />
      <div className="nfs-composer-bar-placeholder">What's on your mind?</div>
    </div>
  );
};

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

const PostComposer = ({ user, communityGroupId, onSuccess, onCancel, onUpgrade }) => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showYouTube, setShowYouTube] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const displayName = user?.nickname || user?.username || '';
  const youtubeId = extractYouTubeId(youtubeUrl);
  const plainText = stripHtml(content).trim();

  const handleSubmit = async () => {
    if (!plainText) { setError('Please write something before posting.'); return; }
    const autoTitle = title.trim() ||
      (plainText.length > 60 ? plainText.slice(0, 57) + '...' : plainText);
    setSubmitting(true);
    setError('');
    try {
      await api.post('/discussion-topics', {
        group_id: communityGroupId,
        title: autoTitle,
        initial_message: content,
        ...(youtubeId ? { youtube_url: youtubeUrl.trim() } : {}),
      });
      setContent('');
      setTitle('');
      setYoutubeUrl('');
      setShowYouTube(false);
      onSuccess();
    } catch (err) {
      if (err.response?.data?.upgrade_required) {
        onUpgrade(err.response.data.error);
        onCancel();
      } else {
        setError(err.response?.data?.error || 'Failed to post. Please try again.');
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="nfs-composer">
      <div className="nfs-composer-header">
        <Avatar name={displayName} />
        <span style={{ fontWeight: 600, color: '#1a202c' }}>{displayName}</span>
      </div>
      {error && <div className="nfs-composer-error">{error}</div>}
      <RichTextEditor
        value={content}
        onChange={setContent}
        placeholder="Share your thoughts, news, or questions about amps..."
      />
      {showYouTube && (
        <div className="nfs-composer-youtube">
          <input
            type="text"
            value={youtubeUrl}
            onChange={e => setYoutubeUrl(e.target.value)}
            placeholder="Paste a YouTube or video link…"
            className="nfs-composer-youtube-input"
          />
          {youtubeId && (
            <img
              src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
              alt="YouTube preview"
              className="nfs-composer-youtube-thumb"
            />
          )}
        </div>
      )}
      <div className="nfs-composer-toolbar">
        <button
          type="button"
          className={`nfs-toolbar-btn${showYouTube ? ' nfs-toolbar-btn--active' : ''}`}
          onClick={() => setShowYouTube(v => !v)}
          title="Add a video link (YouTube or other)"
        >
          ▶ Video Link
        </button>
      </div>
      <div className="nfs-composer-actions">
        <button
          type="button"
          onClick={onCancel}
          style={{ background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !plainText}
          style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', opacity: (submitting || !plainText) ? 0.6 : 1 }}
        >
          {submitting ? 'Posting…' : 'Post'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '24px 16px',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: '0 0 8px 0',
  },
  headerSub: {
    color: '#718096',
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  topicCard: {
    borderLeft: '4px solid #ed8936',
  },
  breadcrumb: {
    fontSize: '11px',
    color: '#a0aec0',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: '600',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a202c',
    marginBottom: '8px',
  },
  cardMeta: {
    display: 'flex',
    gap: '12px',
    fontSize: '13px',
    color: '#718096',
  },
};

const SkeletonCard = () => (
  <div className="nfs-card nfs-skeleton-card">
    <div className="nfs-skeleton-header">
      <div className="nfs-skeleton-avatar" />
      <div className="nfs-skeleton-author-block">
        <div className="nfs-skeleton-line nfs-skeleton-line--short" />
        <div className="nfs-skeleton-line nfs-skeleton-line--xshort" />
      </div>
    </div>
    <div className="nfs-skeleton-line" />
    <div className="nfs-skeleton-line nfs-skeleton-line--medium" />
    <div className="nfs-skeleton-line nfs-skeleton-line--short" />
  </div>
);

const FeedWikiCard = ({ item }) => {
  const { label, icon } = SECTION_LABELS[item.section_type] || { label: item.section_type, icon: '📄' };
  const ampUrl = item.amp_slug && item.manufacturer_slug
    ? `/amps/${item.manufacturer_slug}/${item.amp_slug}#wiki`
    : null;
  const fullAmpName = [item.manufacturer_name, item.amp_name, item.amp_model]
    .filter(Boolean).join(' ');
  const displayName = item.contributor_display_name || 'Unknown';

  return (
    <div className="nfs-card" style={{ ...styles.card, borderLeft: '4px solid #2563eb' }}>
      <div className="nfs-post-header">
        <Avatar name={displayName} size="small" />
        <div className="nfs-post-header-info">
          <span className="nfs-post-author">{displayName}</span>
          <span className="nfs-post-time" title={new Date(item.event_time).toLocaleString()}>
            {timeAgo(item.event_time)}
          </span>
        </div>
      </div>
      <div style={styles.breadcrumb}>
        {icon} Wiki Update · {label}
      </div>
      <div style={{ ...styles.cardTitle, fontSize: '16px' }}>
        {ampUrl
          ? <a href={ampUrl} style={{ color: '#1a202c', textDecoration: 'none' }}
               onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
               onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
              {fullAmpName}
            </a>
          : fullAmpName}
      </div>
      {item.edit_summary && (
        <div style={{ fontSize: '13px', color: '#4a5568', marginTop: '6px', fontStyle: 'italic' }}>
          "{item.edit_summary}"
        </div>
      )}
    </div>
  );
};

const FeedTopicCard = ({ topic, user, isAdmin, onUpgrade, communityGroupId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [topicReactions, setTopicReactions] = useState({ up: 0, down: 0 });
  const [userTopicReaction, setUserTopicReaction] = useState(null);

  useEffect(() => {
    api.get(`/discussion-reactions/counts?entity_type=topic&entity_ids=${topic.topic_id}`)
      .then(r => {
        setTopicReactions((r.data.counts || {})[topic.topic_id] || { up: 0, down: 0 });
        setUserTopicReaction((r.data.user_reactions || {})[topic.topic_id] || null);
      })
      .catch(() => {});
  }, [topic.topic_id]);

  const toggleReaction = async (type) => {
    if (!user) return;
    try {
      const res = await api.post('/discussion-reactions', {
        entity_type: 'topic',
        entity_id: topic.topic_id,
        reaction_type: type,
      });
      setTopicReactions(res.data.counts);
      setUserTopicReaction(res.data.user_reaction);
    } catch (err) {
      console.error(err);
    }
  };

  const isCommunityPost = communityGroupId && topic.group_id === communityGroupId;
  const replyCount = Math.max(0, (topic.message_count || 0) - 1);

  // ── Community "What's on your mind" post ─────────────────────────────────
  // Shows message text + attachment inline — no click-to-expand needed.
  if (isCommunityPost) {
    // Build media list: prefer new attachments table, fall back to legacy single-image
    const atts = topic.first_message_attachments;
    const mediaItems = (atts && atts.length > 0)
      ? atts.map(att => ({
          src: `${API_URL}/discussion-message-attachments/${att.attachment_id}/image`,
          mimeType: att.image_mime_type,
        }))
      : (topic.first_message_has_image && topic.first_message_id)
        ? [{ src: `${API_URL}/discussion-messages/${topic.first_message_id}/image`, mimeType: topic.first_message_mime_type }]
        : [];

    return (
      <div style={{ marginBottom: isExpanded ? 0 : undefined }}>
        <div
          className="nfs-card"
          style={{
            ...styles.card,
            ...styles.topicCard,
            ...(isExpanded ? { borderRadius: '12px 12px 0 0', marginBottom: 0 } : {}),
          }}
        >
          <div className="nfs-post-header">
            <Avatar name={topic.creator_display_name || 'Unknown'} size="small" />
            <div className="nfs-post-header-info">
              <span className="nfs-post-author">{topic.creator_display_name || 'Unknown'}</span>
              <span className="nfs-post-time" title={new Date(topic.created_at).toLocaleString()}>
                {timeAgo(topic.created_at)}
              </span>
            </div>
          </div>
          {topic.first_message_content && (
            <div className="nfs-post-body">{topic.first_message_content}</div>
          )}
          {mediaItems.length > 0 && (
            <div className="nfs-post-media-grid" data-count={String(Math.min(mediaItems.length, 4))}>
              {mediaItems.slice(0, 4).map((item, i) => (
                item.mimeType?.startsWith('video/') ? (
                  <video key={i} src={item.src} controls playsInline className="nfs-post-media-item" />
                ) : (
                  <img key={i} src={item.src} alt={`Attachment ${i + 1}`} className="nfs-post-media-item" onError={e => { e.target.style.display = 'none'; }} />
                )
              ))}
              {mediaItems.length > 4 && (
                <div className="nfs-post-media-overflow">+{mediaItems.length - 4} more</div>
              )}
            </div>
          )}
          {topic.youtube_url && <VideoTopicCard topic={topic} />}
          {topic.amplifier_id && <AmpTopicCard topic={topic} />}
          {topic.latest_message_id && topic.latest_message_id !== topic.first_message_id && (
            <div className="nfs-latest-reply">
              <div className="nfs-latest-reply-header">
                <Avatar name={topic.latest_message_author || 'Unknown'} size="small" />
                <span className="nfs-latest-reply-author">{topic.latest_message_author || 'Unknown'}</span>
                <span className="nfs-latest-reply-time">{timeAgo(topic.latest_message_at)}</span>
              </div>
              {topic.latest_message_content && (
                <CollapsibleMessage content={topic.latest_message_content} />
              )}
            </div>
          )}
          <div style={{
            marginTop: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: '10px',
            paddingTop: '10px',
            borderTop: '1px solid #edf2f7',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {['up', 'down'].map(type => (
                <ReactionButton
                  key={type}
                  type={type}
                  count={topicReactions[type] || 0}
                  active={userTopicReaction === type}
                  onClick={(e) => { e.stopPropagation(); toggleReaction(type); }}
                  disabled={!user}
                  entityType="topic"
                  entityId={topic.topic_id}
                />
              ))}
              <button
                onClick={() => setIsExpanded(v => !v)}
                style={{
                  background: isExpanded ? '#2563eb' : '#e2e8f0',
                  color: isExpanded ? 'white' : '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '4px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.15s, color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                {isExpanded
                  ? '💬 Hide Replies'
                  : replyCount > 0
                    ? `💬 ${replyCount} ${replyCount === 1 ? 'Reply' : 'Replies'}`
                    : '💬 Reply'}
              </button>
            </div>
          </div>
        </div>
        {isExpanded && (
          <InlineTopicThread
            topic={topic}
            user={user}
            isAdmin={isAdmin}
            onUpgrade={onUpgrade}
          />
        )}
      </div>
    );
  }

  // ── Discussion forum topic ─────────────────────────────────────────────
  // Keep original behaviour: click card to expand, title + breadcrumb shown.
  return (
    <div style={{ marginBottom: isExpanded ? 0 : undefined }}>
      <div
        className="nfs-card"
        style={{
          ...styles.card,
          ...styles.topicCard,
          ...(isExpanded ? { borderRadius: '12px 12px 0 0', marginBottom: 0 } : {}),
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(e => !e)}
      >
        <div className="nfs-post-header" onClick={e => e.stopPropagation()}>
          <Avatar name={topic.creator_display_name || 'Unknown'} size="small" />
          <div className="nfs-post-header-info">
            <span className="nfs-post-author">{topic.creator_display_name || 'Unknown'}</span>
            <span className="nfs-post-time" title={new Date(topic.last_post_at || topic.created_at).toLocaleString()}>
              {timeAgo(topic.last_post_at || topic.created_at)}
            </span>
          </div>
        </div>
        <div style={styles.breadcrumb}>
          {topic.category_name} › {topic.group_name}
        </div>
        <div style={styles.cardTitle}>{topic.title}</div>
        {topic.youtube_url && <VideoTopicCard topic={topic} />}
        {topic.amplifier_id && <AmpTopicCard topic={topic} />}
        {topic.latest_message_id && topic.latest_message_id !== topic.first_message_id && (
          <div className="nfs-latest-reply" onClick={e => e.stopPropagation()}>
            <div className="nfs-latest-reply-header">
              <Avatar name={topic.latest_message_author || 'Unknown'} size="small" />
              <span className="nfs-latest-reply-author">{topic.latest_message_author || 'Unknown'}</span>
              <span className="nfs-latest-reply-time">{timeAgo(topic.latest_message_at)}</span>
            </div>
            {topic.latest_message_content && (
              <CollapsibleMessage content={topic.latest_message_content} />
            )}
          </div>
        )}
        <div style={{
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: '10px',
          paddingTop: '10px',
          borderTop: '1px solid #edf2f7',
        }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {['up', 'down'].map(type => (
              <ReactionButton
                key={type}
                type={type}
                count={topicReactions[type] || 0}
                active={userTopicReaction === type}
                onClick={(e) => { e.stopPropagation(); toggleReaction(type); }}
                disabled={!user}
                entityType="topic"
                entityId={topic.topic_id}
              />
            ))}
            <button
              onClick={(e) => { e.stopPropagation(); setIsExpanded(v => !v); }}
              style={{
                background: isExpanded ? '#2563eb' : '#e2e8f0',
                color: isExpanded ? 'white' : '#4a5568',
                border: 'none',
                borderRadius: '8px',
                padding: '4px 14px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              💬 {isExpanded ? 'Hide' : 'View'} Comments ({topic.message_count || 0})
            </button>
          </div>
        </div>
      </div>
      {isExpanded && (
        <InlineTopicThread
          topic={topic}
          user={user}
          isAdmin={isAdmin}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
};

const NewsFeedScreen = () => {
  const { user, isAdmin } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const sentinelRef = useRef(null);
  const seenIds = useRef(new Set());
  const [communityGroupId, setCommunityGroupId] = useState(null);
  const [showComposer, setShowComposer] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [newPostsAvailable, setNewPostsAvailable] = useState(false);
  const composerBarRef = useRef(null);
  const pendingTopicsRef = useRef(null);
  const topicsRef = useRef([]);

  const loadPage = useCallback(async (before) => {
    const url = before ? `/news-feed?before=${encodeURIComponent(before)}` : '/news-feed';
    const r = await api.get(url);
    // Support both paginated { topics, nextBefore, hasMore } and legacy flat-array responses
    const payload = Array.isArray(r.data) ? { topics: r.data, nextBefore: null, hasMore: false } : r.data;
    return {
      topics: payload.topics || [],
      nextBefore: payload.nextBefore,
      hasMore: payload.hasMore,
      communityGroupId: payload.communityGroupId || null,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadPage(null)
      .then(({ topics: newTopics, nextBefore: nb, hasMore: hm, communityGroupId: cgId }) => {
        if (cancelled) return;
        seenIds.current = new Set(newTopics.map(t => t._feed_key || `topic_${t.topic_id}`));
        setTopics(newTopics);
        setNextBefore(nb);
        setHasMore(hm);
        if (cgId) setCommunityGroupId(cgId);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError('Failed to load news feed.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [loadPage]);

  // Infinite scroll — observe a sentinel element near the bottom
  useEffect(() => {
    if (!hasMore || loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && nextBefore) {
          setLoadingMore(true);
          loadPage(nextBefore)
            .then(({ topics: pageTopics, nextBefore: nb, hasMore: hm }) => {
              const newTopics = pageTopics.filter(t => !seenIds.current.has(t._feed_key || `topic_${t.topic_id}`));
              newTopics.forEach(t => seenIds.current.add(t._feed_key || `topic_${t.topic_id}`));
              if (newTopics.length > 0) {
                setTopics(prev => [...prev, ...newTopics]);
              }
              setNextBefore(nb);
              setHasMore(Boolean(hm && nb && nb !== nextBefore));
              setLoadingMore(false);
            })
            .catch(() => {
              setHasMore(false);
              setLoadingMore(false);
            });
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, nextBefore, loadPage]);

  const handlePostSuccess = useCallback(() => {
    setShowComposer(false);
    setNewPostsAvailable(false);
    pendingTopicsRef.current = null;
    setLoading(true);
    setTopics([]);
    setNextBefore(null);
    setHasMore(true);
    seenIds.current = new Set();
    loadPage(null)
      .then(({ topics: newTopics, nextBefore: nb, hasMore: hm }) => {
        seenIds.current = new Set(newTopics.map(t => t._feed_key || `topic_${t.topic_id}`));
        setTopics(newTopics);
        setNextBefore(nb);
        setHasMore(hm);
        setLoading(false);
      })
      .catch(() => { setError('Failed to reload feed.'); setLoading(false); });
  }, [loadPage]);

  // Keep topicsRef in sync so the polling interval never captures stale state
  useEffect(() => { topicsRef.current = topics; }, [topics]);

  // FAB: appear once the composer bar has scrolled off screen
  useEffect(() => {
    const bar = composerBarRef.current;
    if (!bar) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(bar);
    return () => observer.disconnect();
  }); // intentionally no deps — ref is always available after first render

  // Poll every 60 s for new posts from other users
  useEffect(() => {
    if (loading) return;
    const check = async () => {
      if (document.hidden) return;
      const current = topicsRef.current;
      if (current.length === 0) return;
      try {
        const result = await loadPage(null);
        const newFirst = result.topics[0];
        if (newFirst && newFirst._feed_key !== current[0]?._feed_key) {
          pendingTopicsRef.current = result;
          setNewPostsAvailable(true);
        }
      } catch (_) {}
    };
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, [loading, loadPage]);

  const handleNewPostsBanner = useCallback(() => {
    setNewPostsAvailable(false);
    const pending = pendingTopicsRef.current;
    if (pending) {
      seenIds.current = new Set(pending.topics.map(t => t._feed_key || `topic_${t.topic_id}`));
      setTopics(pending.topics);
      setNextBefore(pending.nextBefore);
      setHasMore(pending.hasMore);
      pendingTopicsRef.current = null;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleFabClick = useCallback(() => {
    setShowComposer(true);
    composerBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.headerTitle}>News Feed</h1>
        <div className="nfs-feed-container">
          {[1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#718096' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.headerTitle}>News Feed</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <p style={{ ...styles.headerSub, margin: 0 }}>Recent discussions and wiki updates</p>
        <SetHomepageButton pagePath="/news-feed" />
      </div>
      {!user && (
        <p style={{ fontSize: '14px', color: '#4a5568', marginBottom: '20px' }}>
          <a href="/login" style={{ color: '#2563eb', fontWeight: 600 }}>Log in</a>{' '}
          or{' '}
          <a href="/register" style={{ color: '#2563eb', fontWeight: 600 }}>sign up</a>{' '}
          to react and join the conversation.
        </p>
      )}
      <div ref={composerBarRef}>
        {user && communityGroupId && !showComposer && (
          <WhatsOnYourMindBar user={user} onClick={() => setShowComposer(true)} />
        )}
        {user && communityGroupId && showComposer && (
          <PostComposer
            user={user}
            communityGroupId={communityGroupId}
            onSuccess={handlePostSuccess}
            onCancel={() => setShowComposer(false)}
            onUpgrade={(msg) => { setUpgradeMessage(msg); setShowUpgradeModal(true); }}
          />
        )}
      </div>
      {newPostsAvailable && (
        <div className="nfs-new-posts-banner" onClick={handleNewPostsBanner} role="button" tabIndex={0}>
          ↑ New posts — tap to refresh
        </div>
      )}
      <div className="nfs-feed-container">
      {topics.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#718096' }}>
          No recent activity.
        </div>
      ) : (
        <>
          {topics.map(item => (
            item.item_type === 'wiki'
              ? <FeedWikiCard key={item._feed_key} item={item} />
              : <FeedTopicCard
                  key={item._feed_key || `topic_${item.topic_id}`}
                  topic={item}
                  user={user}
                  isAdmin={isAdmin}
                  communityGroupId={communityGroupId}
                  onUpgrade={(msg) => { setUpgradeMessage(msg); setShowUpgradeModal(true); }}
                />
          ))}
          {/* Sentinel for infinite scroll */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#718096' }}>
              Loading more...
            </div>
          )}
          {!hasMore && topics.length > 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#a0aec0', fontSize: '14px' }}>
              You've reached the end of the feed.
            </div>
          )}
        </>
      )}
      </div>
      <UpgradeModal
        show={showUpgradeModal}
        message={upgradeMessage}
        onClose={() => setShowUpgradeModal(false)}
      />
      {user && communityGroupId && !showComposer && showFab && (
        <button className="nfs-fab" onClick={handleFabClick} aria-label="Create post">+</button>
      )}
    </div>
  );
};

export default NewsFeedScreen;
